import { useMemo, useState } from 'react';
import { CATALOG, type FunctionDoc } from '@/engine/catalog';

const GROUPS: FunctionDoc['group'][] = ['Giraffe', 'Math', 'Logic', 'Lookup', 'Text', 'Date', 'Finance'];

const SHORTCUTS: [string, string][] = [
  ['Enter / F2', 'Edit the selected cell'],
  ['Tab / Shift+Tab', 'Move right / left'],
  ['Cmd/Ctrl + arrow', 'Jump to the edge of the data'],
  ['Shift + arrow', 'Extend the selection'],
  ['Cmd/Ctrl + D / R', 'Fill down / right'],
  ['Drag the corner handle', 'Fill, continuing a series (1,2,3 → 4,5,6; Jan → Feb)'],
  ['Ctrl/Cmd while dragging it', 'Repeat instead of continuing'],
  ['Right-click', 'Colours, borders and number format'],
  ['Cmd/Ctrl + C / X / V', 'Copy / cut / paste (TSV — round-trips with Excel)'],
  ['Cmd/Ctrl + Z', 'Undo'],
  ['Delete', 'Clear the selection (on a database row, deletes the database)'],
  ['Right-click a database sub-row', 'Select or zoom to those objects on the map'],
  ['Type in any field list', 'Search it — every list of Giraffe fields or values has a search box'],
  ['Cmd/Ctrl + \\', 'Clear formatting and dropdowns'],
];

export function HelpPane({ onClose }: { onClose: () => void }): JSX.Element {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return CATALOG;
    return CATALOG.filter(
      (f) =>
        f.name.includes(q) ||
        f.summary.toUpperCase().includes(q) ||
        f.group.toUpperCase().includes(q) ||
        // "netArea" finds GI.TOTAL, which is how someone looks for it.
        f.examples?.some((e) => e.toUpperCase().includes(q))
    );
  }, [query]);

  return (
    <aside className="gt-pane">
      <header className="gt-pane-head">
        <h2>Functions</h2>
        <button className="gt-btn" onClick={onClose} title="Close">×</button>
      </header>

      <div className="gt-pane-body">
        <input
          className="gt-input"
          placeholder="Search functions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <p className="gt-note">
          {CATALOG.length} functions — the set a proforma, an area schedule and a unit mix actually
          need. Type <code>=</code> in a cell and the name to autocomplete.
        </p>

        {GROUPS.map((group) => {
          const rows = filtered.filter((f) => f.group === group);
          if (rows.length === 0) return null;
          return (
            <section key={group} className="gt-help-group">
              <h3>{group}</h3>
              {rows.map((f) => (
                <div key={f.name} className="gt-help-row">
                  <code>{f.signature}</code>
                  <span>{f.summary}</span>
                  {f.examples?.map((example) => (
                    <code key={example} className="gt-help-example">
                      {example}
                    </code>
                  ))}
                </div>
              ))}
            </section>
          );
        })}

        <section className="gt-help-group">
          <h3>Field names</h3>
          <p className="gt-note">
            <code>GI.*</code> functions read the <b>Areas sheet's rows</b> — one per level of each
            baked stack — and the first argument is a property of those rows, spelled exactly as
            the project spells it. The engine pre-computes{' '}
            <code>area</code> and <code>perimeter</code> (one level), <code>grossArea</code>,{' '}
            <code>netArea</code>, <code>saleableArea</code> (levels already applied), and merges
            the usage's own <code>salePrice</code>, <code>hardCost</code>, <code>efficiency</code>,{' '}
            <code>floorToFloor</code> onto every row.
          </p>
          <div className="gt-help-row">
            <code>dwelling-&lt;mix key&gt;-Count</code>
            <span>
              The dwelling mix is <b>flattened, one property per key you typed into the mix</b> —
              so it is <code>dwelling-1 bed-Count</code> in a project whose mix says “1 bed”, and{' '}
              <code>dwelling-1BED-Count</code> in one that says “1BED”. The same four families
              exist for <code>PricePer</code>, <code>ResidentsPer</code> and{' '}
              <code>ParkingRequiredPer</code>.
            </span>
          </div>
          <div className="gt-help-row">
            <code>dwellingTotalCount</code>
            <span>
              The rollups, which every project has whatever its mix keys are:{' '}
              <code>dwellingTotalCount</code>, <code>dwellingTotalPrice</code>,{' '}
              <code>dwellingTotalResidents</code>, <code>dwellingTotalParkingRequired</code>.
            </span>
          </div>
          <p className="gt-note">
            A field <b>nobody carries answers 0</b>, not an error — so a misspelling is a plausible
            number rather than a complaint. The status bar counts the warning, and where the
            spelling is close the cell says which field it thinks you meant. Typing{' '}
            <code>=GI.TOTAL(&quot;</code> lists the fields this project actually has.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Filters</h3>
          <p className="gt-note">
            The second argument of most <code>GI.*</code> functions is a filter, and it is{' '}
            <b>a piece of text</b> — that one fact explains every quote mark you will see.
          </p>
          <div className="gt-help-row">
            <code>field op value</code>
            <span>
              One clause. <code>usage=Residential</code>, <code>levels&gt;3</code>,{' '}
              <code>name~tower</code>. No quotes around the value.
            </span>
          </div>
          <div className="gt-help-row">
            <code>= != &gt; &lt; &gt;= &lt;= ~</code>
            <span>
              The operators. <code>~</code> is “contains”.
            </span>
          </div>
          <div className="gt-help-row">
            <code>a, b</code>
            <span>Comma-separate to AND them: <code>usage=Retail, levels&gt;3</code>.</span>
          </div>

          <h4 className="gt-help-sub">Taking the value from a cell</h4>
          <p className="gt-note">
            This is the bit that reads strangely, so it is worth saying plainly. The filter is
            text, so <code>usage=</code> needs quotes like any text, and a value living in a cell
            has to be <i>joined onto</i> it — which in a spreadsheet is <code>&amp;</code>:
          </p>
          <div className="gt-help-row">
            <code>"usage="&amp;$A20</code>
            <span>Reads: the text <code>usage=</code>, followed by whatever A20 says.</span>
          </div>
          <p className="gt-note">
            <code>GI.WHERE</code> does the same thing without the arithmetic, and is what a
            stamped schedule writes:
          </p>
          <div className="gt-help-row">
            <code>GI.WHERE("usage", $A20)</code>
            <span>Field, then value. Pairs for more: <code>GI.WHERE("usage", $A20, "levels&gt;", 3)</code>.</span>
          </div>
          <div className="gt-help-row">
            <code>GI.SUMEACH("grossArea * hardCost", GI.WHERE("usage", $A20))</code>
            <span>The whole thing: cost of every section whose usage is named in A20.</span>
          </div>
          <p className="gt-note">
            A value with a comma in it — “Retail, ground” — would otherwise split the filter in
            two. <code>GI.WHERE</code> quotes those and leaves the rest bare, and a filter typed
            by hand is forgiven: a fragment after a comma with no operator belongs to the value
            before it.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Sheet filter</h3>
          <p className="gt-note">
            <b>Filter</b> in the toolbar opens a bar above the grid. Pick a layer, a usage or any
            other field and <b>every GI formula and every database on that sheet</b> applies it —
            other sheets are untouched. The bar turns blue while anything is picked, so a filtered
            sheet never passes for the whole project.
          </p>
          <div className="gt-help-row">
            <code>any</code>
            <span>A field left on “any” filters nothing. <b>+ Field</b> adds another; they AND together.</span>
          </div>
          <div className="gt-help-row">
            <code>=GI.TOTAL("grossArea", "usage=Retail")</code>
            <span>
              A formula's own filter ANDs with the sheet's. On a sheet filtered to layer 2, this is
              the Retail on layer 2.
            </span>
          </div>
          <p className="gt-note">
            A field a source does not carry is skipped rather than matching nothing, so a usages
            table on a sheet filtered by layer still lists the usages. <b>Remove filter</b> (or the
            button again) takes it off; Cmd/Ctrl+Z undoes any change.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Databases</h3>
          <p className="gt-note">
            <b>Database</b> in the toolbar lists the project's objects the way a Vectorworks
            worksheet database does. Step 1 picks <b>which</b> objects — criteria like “layer is 2”,
            “usage is not Retail”, “name contains tower”. Step 2 picks the <b>columns</b>; a field
            can be added twice, summed in one and averaged in the next.
          </p>
          <div className="gt-help-row">
            <code>11 · 11.1 · 11.2 …</code>
            <span>
              A database takes <b>one real row</b> (always from column A) holding the summary as
              ordinary GI formulas. Its objects hang beneath as sub-rows 11.1, 11.2 — outside the
              sheet's numbering, so row 12 stays row 12 and <b>references below never move</b>, however
              many objects a bake adds.
            </span>
          </div>
          <div className="gt-help-row">
            <code>▾ on the database row</code>
            <span>
              Per column: group by it (one sub-row per group, summarised), change the summary (sum,
              average, count…), duplicate or remove the column, edit, delete.
            </span>
          </div>
          <div className="gt-help-row">
            <code>▾ / ▸ by the row number</code>
            <span>Collapse to the database row alone, or show the sub-rows again.</span>
          </div>
          <div className="gt-help-row">
            <code>Right-click a sub-row</code>
            <span>Select those objects on the map, or zoom to them. Grouped, a sub-row is the whole group.</span>
          </div>
          <div className="gt-help-row">
            <code>Delete a database</code>
            <span>
              Select its row and press Delete, or right-click the row number, a sub-row or the ▾ —
              <b> Delete database</b>. The titles above are ordinary cells and stay.
            </span>
          </div>
          <p className="gt-note">
            Placed on a row that is not empty, a database inserts a fresh row rather than writing
            over it. A text column shows its value when every object agrees and “14 objects” when
            they do not; objects with no value in a grouped field are grouped as “(no usage)”.
            Sub-rows are read-only and cannot be referenced — reference the database row instead.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Sheets</h3>
          <div className="gt-help-row">
            <code>×  on a tab</code>
            <span>
              Delete the sheet — any ordinary one, even the last (a blank one takes its place).
              Cmd/Ctrl+Z brings it back. Areas, Features, Blocks and Usages cannot be deleted.
            </span>
          </div>
          <div className="gt-help-row">
            <code>File ▸ Save a .json copy</code>
            <span>The whole workbook — cells, charts, databases, filters — as JSON. File ▸ Import reads it back.</span>
          </div>
        </section>

        <section className="gt-help-group">
          <h3>Measure classes</h3>
          <p className="gt-note">
            The Areas sheet splits each stack into one row per level. <b>Plan</b> measures
            (<code>area</code>, <code>perimeter</code>) are one level's worth and repeat on every
            level, so adding them up counts a 10-level plate 10 times — the same as a SUM down the
            sheet. <b>Total</b> measures (<code>grossArea</code>, <code>netArea</code>, unit counts)
            are split across the levels and add back to the stack's figure. <b>Rate</b> measures (<code>salePrice</code>,
            <code>hardCost</code>, <code>efficiency</code>) are per-unit and cannot meaningfully be
            summed — gTables will still sum them if you ask, and flag it. Use{' '}
            <code>GI.WEIGHTEDAVG()</code> for the area-weighted mean instead.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Units</h3>
          <p className="gt-note">
            Giraffe's own numbers are <b>metric</b> — m² and metres — whatever the
            project is set to display. gTables reads the project's setting and respells them on
            the way into a cell, so a project in feet reads in feet. The toolbar's{' '}
            <b>Units</b> control follows the project by default and can pin either system; the
            choice is saved with the workbook, because a stamped header is ordinary text that
            gets shared with it.
          </p>
          <div className="gt-help-row">
            <code>area, grossArea, netArea…</code>
            <span>Areas — m² or ft².</span>
          </div>
          <div className="gt-help-row">
            <code>perimeter, height, floorToFloor</code>
            <span>Lengths — m or ft.</span>
          </div>
          <div className="gt-help-row">
            <code>salePrice, rentPrice, hardCost, softCost</code>
            <span>
              Money rates — <b>never</b> converted. They are the numbers typed into the usage,
              already per ft² on an imperial project, so they are shown exactly as entered.
            </span>
          </div>
          <p className="gt-note">
            Counts, <code>levels</code>, <code>efficiency</code> and the dwelling mix are
            dimensionless and never converted. Neither is a field the app does not recognise — it
            shows the project's own number and its header says no unit, which is the honest
            answer. Columns whose header names a unit are the converted ones.{' '}
            <code>=GI.UNITS()</code> answers “metric” or “imperial” in a cell.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Dates</h3>
          <p className="gt-note">
            A typed date becomes an <b>Excel serial number</b> with a date format, so{' '}
            <code>YEAR</code>, <code>EOMONTH</code> and <code>XNPV</code> all read it. Typing
            follows your locale for <code>3/4/2026</code>; <code>2026-03-04</code> and{' '}
            <code>4 Mar 2026</code> are unambiguous everywhere. Dragging a date fills a series —
            by day, week or month, whichever the source is doing.
          </p>
        </section>

        <section className="gt-help-group">
          <h3>Shortcuts</h3>
          {SHORTCUTS.map(([keys, what]) => (
            <div key={keys} className="gt-help-row">
              <code>{keys}</code>
              <span>{what}</span>
            </div>
          ))}
        </section>

        <section className="gt-help-group">
          <h3>Advanced: other sources</h3>
          <p className="gt-note">
            Everything above reads the <b>Areas</b> tab's rows, and that is what you want almost
            always. A <code>GI.*</code> call can instead read one of Giraffe's own sources by
            naming it as the argument after the filter:
          </p>
          <div className="gt-help-row">
            <code>bakedSections</code>
            <span>
              Giraffe's engine output, one row per baked stack rather than per level.{' '}
              <code>=GI.TOTAL("grossArea", "", "bakedSections")</code>
            </span>
          </div>
          <div className="gt-help-row">
            <code>rawSections</code>
            <span>What was drawn, before the engine stacked it.</span>
          </div>
          <div className="gt-help-row">
            <code>usages</code>
            <span>The project usage table — rents, costs, efficiency.</span>
          </div>
          <p className="gt-note">
            The source always goes <b>last, after the filter</b> — pass <code>""</code> as the
            filter when you do not need one. Field names differ between sources: the top of the
            autocomplete list says which source it is showing, and switches as soon as the
            formula names one, even when you go back to edit the field.
          </p>
        </section>
      </div>
    </aside>
  );
}
